/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { MenuItem } from "@chakra-ui/react";
import { CapitalActivityAdjustmentsModal } from "features/capital-activity/event/adjustments/CapitalActivityAdjustmentsModal";
import { useAdjustmentsModal } from "features/capital-activity/event/useAdjustmentsModal";
import {
  getPortfolioTransactionListAdjustmentsTableQueryKey,
  useGetDraftPortfolioEventSuspense,
  usePortfolioTransactionListAdjustmentsTableSuspense,
} from "gen/endpoints/portfolio-transactions/portfolio-transactions";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { suppressAutoSizePinnedColumns } from "shared/components/table/data/DataTableUtils";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const DraftAllocationsPage: React.FC = () => {
  const { t } = useTranslation(["common", "fees"]);

  const invalidateQueries = useInvalidateQueryKeys();
  const { fundFamilyId } = useRequiredStringParams();

  const portfolioTxDraftEvent = useGetDraftPortfolioEventSuspense(fundFamilyId);

  const readonlyTable = usePortfolioTransactionListAdjustmentsTableSuspense(
    fundFamilyId,
    {
      is_read_only: true,
      is_draft_tx: true,
      fund_family_event_id: portfolioTxDraftEvent.data.draft_tx!.id!,
    },
  );

  const editableTable = usePortfolioTransactionListAdjustmentsTableSuspense(
    fundFamilyId,
    {
      is_read_only: false,
      is_draft_tx: true,
      fund_family_event_id: portfolioTxDraftEvent.data.draft_tx!.id!,
    },
  );

  const postSubmit = useCallback(async () => {
    await invalidateQueries(
      getPortfolioTransactionListAdjustmentsTableQueryKey(fundFamilyId, {
        is_read_only: true,
        fund_family_event_id: portfolioTxDraftEvent.data.draft_tx!.id!,
        is_draft_tx: true,
      }),
      getPortfolioTransactionListAdjustmentsTableQueryKey(fundFamilyId, {
        is_read_only: false,
        fund_family_event_id: portfolioTxDraftEvent.data.draft_tx!.id!,
        is_draft_tx: true,
      }),
    );
  }, [fundFamilyId, invalidateQueries, portfolioTxDraftEvent.data.draft_tx]);

  const { onSubmit } = useAdjustmentsModal({
    eventData: portfolioTxDraftEvent.data.draft_tx!,
    postSubmit,
  });

  const {
    value: isAddAdjustmentsModalOpen,
    setTrue: openAddAdjustmentsModal,
    setFalse: closeAddAdjustmentsModal,
  } = useBoolean(false);

  return (
    <PageContent contentWidth="wide" isFullHeight={true}>
      <PageHeader
        backButton={{
          title: t("fees:feeUseBreakdown.allocations.backNav"),
        }}
        title={t("fees:feeUseBreakdown.allocations.title")}
        actions={
          <Restricted permission={PermissionTypeEnum.write}>
            <PageMenu>
              <MenuItem onClick={openAddAdjustmentsModal}>
                {t("capitalActivity:adjustments.title")}
              </MenuItem>
            </PageMenu>
          </Restricted>
        }
      />
      <PageBody>
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:feeUseBreakdown.allocations.subtitle")}
            </TableBoxTitle>
            <DataTableBoxActions
              showColControl={false}
              showFullScreen={false}
            />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              suppressStatusBar={true}
              data={readonlyTable.data}
              isLoading={readonlyTable.isFetching}
              getColumnDef={suppressAutoSizePinnedColumns}
            />
          </TableBoxBody>
        </TableBox>
      </PageBody>

      <CapitalActivityAdjustmentsModal
        onSubmit={onSubmit}
        isLoading={editableTable.isFetching}
        tableData={editableTable.data}
        isOpen={isAddAdjustmentsModalOpen}
        onClose={closeAddAdjustmentsModal}
      />
    </PageContent>
  );
};
export default DraftAllocationsPage;
